import { useRef, useState, useEffect, useCallback } from "react";
import { useDroppable, useDndMonitor } from "@dnd-kit/core";
import { type DropSpot, type DepthOption, resolveDepthFromX, INDENT_PX } from "@/lib/tree-utils";

interface DropSpotIndicatorProps {
  spot: DropSpot;
  variant?: "accent" | "danger";
}

const styles = {
  accent: {
    dot: "bg-app-accent/40",
    dotOver: "bg-app-accent",
    line: "bg-app-accent/20",
    lineOver: "bg-app-accent",
    label: "text-app-accent bg-app-accent/10",
  },
  danger: {
    dot: "bg-app-danger/40",
    dotOver: "bg-app-danger",
    line: "bg-app-danger/20",
    lineOver: "bg-app-danger",
    label: "text-app-danger bg-app-danger/10",
  },
} as const;

export function DropSpotIndicator({ spot, variant = "accent" }: DropSpotIndicatorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [resolvedOption, setResolvedOption] = useState<DepthOption>(spot.depthOptions[0]!);
  const resolvedRef = useRef<DepthOption>(spot.depthOptions[0]!);

  // Keep the droppable data in sync with the latest resolved option
  const { setNodeRef, isOver } = useDroppable({
    id: spot.id,
    data: { ...spot, ...resolvedRef.current },
  });

  // Combine refs for the container element
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);

  // Track pointer X and resolve depth when hovered
  useDndMonitor({
    onDragMove(event) {
      if (!isOver || !containerRef.current) return;
      const pointerX = event.activatorEvent instanceof PointerEvent
        ? (event.activatorEvent as PointerEvent).clientX + (event.delta?.x ?? 0)
        : 0;
      if (!pointerX) return;

      const rect = containerRef.current.getBoundingClientRect();
      const option = resolveDepthFromX(spot, pointerX, rect.left, INDENT_PX);
      if (option.depth !== resolvedRef.current.depth) {
        resolvedRef.current = option;
        setResolvedOption(option);
      }
    },
  });

  // Reset resolved option when spot changes
  useEffect(() => {
    resolvedRef.current = spot.depthOptions[0]!;
    setResolvedOption(spot.depthOptions[0]!);
  }, [spot]);

  const c = styles[variant];
  const displayDepth = isOver ? resolvedOption.depth : spot.minDepth;

  return (
    <div
      ref={combinedRef}
      className="h-2 relative"
    >
      {isOver && (
        <div
          className="absolute inset-y-0 flex items-center gap-1 transition-[padding] duration-100"
          style={{
            left: displayDepth > 0 ? `${Math.min(displayDepth * 1.5, 6)}rem` : 0,
            right: 0,
          }}
        >
          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${c.dotOver}`} />
          <div className={`flex-1 rounded-full h-0.5 ${c.lineOver}`} />
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md shrink-0 ${c.label}`}>
            {resolvedOption.parentKey ? "↳ Nest" : "Root"}
          </span>
        </div>
      )}
    </div>
  );
}
